/**
 * HTTP failure classification, shared by the QPL instrumentation in
 * `client.ts` and the transport-retry policy in `retry.ts`.
 *
 * Turns an axios failure into the queryable shape the `http_error` point
 * carries. The distinction that matters most is `reachedServer`: when it's
 * false the request never produced a server-side response (offline,
 * connection refused, DNS, backend down, or a client-side timeout), so
 * there is NO stitched server flow in QPL and the failure is not a
 * saga/routing/auth defect. axios 1.x sets `error.code` to ERR_NETWORK /
 * ECONNABORTED / ETIMEDOUT / ERR_CANCELED on these.
 */

import type { AxiosError } from 'axios';

export type HttpErrorKind = 'http' | 'timeout' | 'network' | 'canceled' | 'unknown';

export interface HttpErrorInfo {
  kind: HttpErrorKind;
  /** HTTP status when a response arrived; 0 when the request never got one. */
  status: number;
  /** Envelope error code (when the server responded) or the axios error code. */
  code: string;
  /** True only when the server produced an HTTP response. */
  reachedServer: boolean;
}

export function classifyHttpError(error?: AxiosError): HttpErrorInfo {
  if (!error) return { kind: 'unknown', status: 0, code: '', reachedServer: false };

  // A response object means the request reached the server and it
  // answered with a non-2xx — a real HTTP error (500/409/401/...).
  if (error.response) {
    const envCode = (error.response.data as any)?.error?.code;
    return {
      kind: 'http',
      status: error.response.status,
      code: envCode || error.code || '',
      reachedServer: true,
    };
  }

  // No response: the request died before the server answered.
  const code = error.code || '';
  let kind: HttpErrorKind = 'unknown';
  if (code === 'ECONNABORTED' || code === 'ETIMEDOUT') kind = 'timeout';
  else if (code === 'ERR_CANCELED') kind = 'canceled';
  else if (code === 'ERR_NETWORK' || /network/i.test(error.message || '')) kind = 'network';
  return { kind, status: 0, code, reachedServer: false };
}
